import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './store/authStore'
import Layout from './components/layout/Layout'

// Auth pages
import LoginPage from './pages/auth/LoginPage'
import RegisterPage from './pages/auth/RegisterPage'
import VerifyEmailPage from './pages/auth/VerifyEmailPage'
import GoogleCallbackPage from './pages/auth/GoogleCallbackPage'
import ChangePasswordPage from './pages/auth/ChangePasswordPage'

// Learn pages
import CourseCatalogPage from './pages/learn/CourseCatalogPage'
import CourseDetailPage from './pages/learn/CourseDetailPage'
import ProgramDetailPage from './pages/learn/ProgramDetailPage'
import LessonViewerPage from './pages/learn/LessonViewerPage'
import DashboardPage from './pages/learn/DashboardPage'
import CertificatesPage from './pages/learn/CertificatesPage'

// Instructor pages
import InstructorDashboardPage from './pages/instructor/InstructorDashboardPage'
import CourseEditorPage from './pages/instructor/CourseEditorPage'

// Admin pages
import AdminDashboardPage from './pages/admin/AdminDashboardPage'

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { accessToken, mustChangePassword } = useAuthStore()
  if (!accessToken) return <Navigate to="/login" replace />
  if (mustChangePassword && window.location.pathname !== '/change-password')
    return <Navigate to="/change-password" replace />
  return <>{children}</>
}

function RequireInstructor({ children }: { children: React.ReactNode }) {
  const { accessToken, user } = useAuthStore()
  if (!accessToken) return <Navigate to="/login" replace />
  if (user?.role !== 'instructor' && user?.role !== 'admin') return <Navigate to="/learn" replace />
  return <>{children}</>
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { accessToken, user, mustChangePassword } = useAuthStore()
  if (!accessToken) return <Navigate to="/login" replace />
  if (mustChangePassword) return <Navigate to="/change-password" replace />
  if (user?.role !== 'admin') return <Navigate to="/learn" replace />
  return <>{children}</>
}

export default function App() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
      <Route path="/change-password" element={<RequireAuth><ChangePasswordPage /></RequireAuth>} />

      {/* Main layout */}
      <Route element={<Layout />}>
        <Route path="/" element={<Navigate to="/learn" replace />} />
        <Route path="/learn" element={<CourseCatalogPage />} />
        <Route path="/programs/:slug" element={<ProgramDetailPage />} />
        <Route path="/learn/:slug" element={<CourseDetailPage />} />
        <Route path="/learn/:slug/lessons/:lessonId" element={<LessonViewerPage />} />
        <Route path="/dashboard" element={
          <RequireAuth><DashboardPage /></RequireAuth>
        } />
        <Route path="/certificates" element={
          <RequireAuth><CertificatesPage /></RequireAuth>
        } />
        <Route path="/instructor" element={
          <RequireInstructor><InstructorDashboardPage /></RequireInstructor>
        } />
        <Route path="/instructor/courses/:slug/edit" element={
          <RequireInstructor><CourseEditorPage /></RequireInstructor>
        } />
        <Route path="/admin" element={
          <RequireAdmin><AdminDashboardPage /></RequireAdmin>
        } />
      </Route>
    </Routes>
  )
}
